import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIReceptionistWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi — I'm Viyora AI, your product assistant for Viyora, an AI-powered clinic management platform. Ask me about features, pricing, plans, WhatsApp automation, AI Copilot, digital consents, dental charting, onboarding, integrations — or anything else about Viyora." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `recep-${Math.random().toString(36).slice(2, 10)}`);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai/receptionist`, { session_id: sessionId, message: text });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry — I'm having trouble right now. Please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="ai-receptionist-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-pine text-linen shadow-[0_16px_48px_rgba(26,54,43,0.28)] grid place-items-center hover:bg-pine-mid transition-all active:scale-95"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
      {open && (
        <div data-testid="ai-receptionist-panel" className="fixed bottom-6 right-6 z-40 w-[360px] max-w-[95vw] h-[520px] card-glass flex flex-col p-0 overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between bg-pine text-linen">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <div>
                <div className="font-display leading-none">Viyora AI</div>
                <div className="text-[10px] uppercase tracking-widest opacity-70">Product assistant · Online</div>
              </div>
            </div>
            <button data-testid="ai-receptionist-close" onClick={() => setOpen(false)} className="opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-auto p-4 space-y-3 bg-white/60">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-pine text-linen rounded-br-sm" : "bg-linen text-ink rounded-bl-sm"
                }`}>{m.content}</div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-linen text-ink-mid rounded-2xl px-3 py-2 text-sm inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Typing…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[#E8EAE9] bg-white flex gap-2">
            <input data-testid="ai-receptionist-input" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about Viyora features, pricing…"
              className="flex-1 rounded-full bg-linen px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
            <button data-testid="ai-receptionist-send" disabled={loading} className="w-10 h-10 rounded-full bg-pine text-linen grid place-items-center hover:bg-pine-mid disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
